import { updateWorkflowTemplateVersion } from '@commenda/api';
import type { SendAgentConversationMessageBody } from '@commenda/api/commenda-ai/types';
import { AgentMode, BuilderChatContext } from '@commenda/types';
import { useCallback } from 'react';
import { TBuilderCanvasBinding } from './types/types';

const SAVE_FAILED_MESSAGE =
  'Could not save your canvas edits, so the agent may be working from the last saved version.';

/**
 * What a builder turn needs before and during a send.
 *
 * `beforeSend` is awaited by the shared composer, which is where the canvas save belongs: a turn
 * posted while the save is still in flight can reach the agent naming a version that does not yet
 * hold the author's edits. `buildSendBody` is read synchronously after it and carries the fields.
 */
export function useBuilderSendBody(
  params: Readonly<{
    mode: AgentMode;
    modelId: string;
    /** Always a draft by the time a turn can write: the panel forks before it lets Agent mode be
     *  chosen on a published version. */
    targetWorkflowTemplateVersionId: string;
    isEditable: boolean;
    canvas: TBuilderCanvasBinding;
    getBuilderContext: () => BuilderChatContext | null;
    /** Clears the per-message context suppression, which only ever covers the next send. */
    onContextSent: () => void;
    onSaveFailed: (message: string) => void;
  }>
): {
  beforeSend: () => Promise<boolean>;
  buildSendBody: () => Partial<SendAgentConversationMessageBody>;
} {
  const {
    mode,
    modelId,
    targetWorkflowTemplateVersionId,
    isEditable,
    canvas,
    getBuilderContext,
    onContextSent,
    onSaveFailed,
  } = params;

  // A failed save warns and lets the turn through, which is what the warning says happens: the
  // agent works from the last saved version rather than the send being silently dropped.
  const beforeSend = useCallback(async (): Promise<boolean> => {
    // Never save state that arrived from a poll: applyRemoteWorkflow resets the baseline, so unsaved
    // changes are only ever the author's own edits and a blind save would be a revert.
    const definition = canvas.workflow;
    if (!canvas.hasUnsavedChanges() || !definition || !isEditable) return true;

    try {
      const result = await updateWorkflowTemplateVersion(
        targetWorkflowTemplateVersionId,
        { definition }
      );
      if (result.ok) canvas.markAsSaved();
      else onSaveFailed(SAVE_FAILED_MESSAGE);
    } catch {
      onSaveFailed(SAVE_FAILED_MESSAGE);
    }

    return true;
  }, [targetWorkflowTemplateVersionId, isEditable, canvas, onSaveFailed]);

  const buildSendBody = useCallback(() => {
    const builderContext = getBuilderContext();
    onContextSent();

    return {
      mode,
      modelId,
      targetWorkflowTemplateVersionId,
      ...(builderContext ? { builderContext } : {}),
    };
  }, [
    mode,
    modelId,
    targetWorkflowTemplateVersionId,
    getBuilderContext,
    onContextSent,
  ]);

  return { beforeSend, buildSendBody };
}
